"use client";

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { ru } from './locales/ru';
import { uz } from './locales/uz';
import { en } from './locales/en';

export const SUPPORTED_LANGUAGES = ['ru', 'uz', 'en'] as const;
export const DEFAULT_LANGUAGE = 'ru';
// Same key i18next-browser-languagedetector used, so saved choices carry over.
const STORAGE_KEY = 'i18nextLng';

// Always start in the default language: the landing page is server-rendered,
// and the first client render must match that HTML. The visitor's language is
// applied after hydration by `applyPreferredLanguage`.
i18n
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      uz: { translation: uz },
      en: { translation: en },
    },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    initAsync: false,
    interpolation: {
      escapeValue: false,
    },
  });

const toSupported = (lang: string | null | undefined) => {
  const base = lang?.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.find((l) => l === base);
};

const detectLanguage = (): string => {
  try {
    const fromQuery = toSupported(new URLSearchParams(window.location.search).get('lng'));
    if (fromQuery) return fromQuery;
    const saved = toSupported(window.localStorage.getItem(STORAGE_KEY));
    if (saved) return saved;
  } catch {
    /* storage unavailable */
  }
  for (const lang of navigator.languages ?? [navigator.language]) {
    const match = toSupported(lang);
    if (match) return match;
  }
  return DEFAULT_LANGUAGE;
};

/** Browser only: switch to the visitor's language and remember later changes. */
export const applyPreferredLanguage = () => {
  const onChange = (lang: string) => {
    document.documentElement.lang = lang;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable */
    }
  };
  i18n.on('languageChanged', onChange);
  const ready = i18n.changeLanguage(detectLanguage());
  return { ready, dispose: () => i18n.off('languageChanged', onChange) };
};

export default i18n;
